// Pure helpers for AI Studio's inline image edit (POR-14685): which image an
// `edit_image` call works on, the prompt sent to Gemini, file names, and how an
// edit interrupted by a reload looks. No imports, so `node --test` can load it.

export const IMAGE_EDIT_INTERRUPTED = 'This edit was interrupted. Please try again.';

function isImageUrl(att) {
  return !!(att && att.type === 'url' && typeof att.payload === 'string' && att.payload);
}

function attachmentName(att) {
  if (!att) return '';
  return att.name || (att.payload && typeof att.payload === 'object' && att.payload.name) || '';
}

/**
 * The image to edit.
 *
 * `savedAttachments` are the turn's images as the server stored them: first the
 * user's own attachments (as many as `userAttachments`), then possibly the last
 * generated image that sendMessage injects — never treat that one as attached.
 * `imageIndex` picks one of the user's own; with none attached, the newest image
 * the assistant produced is edited, so "Now make the cap gold" chains edits.
 */
export function resolveImageEditSource({
  imageIndex = null,
  savedAttachments = [],
  userAttachments = [],
  messages = [],
} = {}) {
  const ownCount = Array.isArray(userAttachments) ? userAttachments.length : 0;
  const own = (savedAttachments || [])
    .slice(0, ownCount)
    .map((att, index) => ({ att, index }))
    .filter(({ att }) => isImageUrl(att));

  if (own.length) {
    const chosen = own.find(({ index }) => index === imageIndex) || own[0];

    return { url: chosen.att.payload, name: attachmentName(userAttachments[chosen.index]) };
  }

  for (let i = (messages || []).length - 1; i >= 0; i -= 1) {
    const message = messages[i];

    if (!message || message.role !== 'assistant' || !Array.isArray(message.attachments)) continue;

    const image = message.attachments.find(isImageUrl);

    if (image) return { url: image.payload, name: image.name || '' };
  }

  return null;
}

export function buildImageEditPrompt(instruction) {
  return [
    'Edit IMAGE 1.',
    `Apply ONLY this change: ${String(instruction || '').trim()}`,
    'Keep everything else exactly as it is: composition, framing, product, colors, lighting, background, '
      + 'and the typography style, font, size and placement of all text.',
    'Any new or replaced text must be spelled exactly as requested, character for character.',
    'Do not add any new text, logos, borders or elements that were not requested.',
  ].join('\n');
}

// "17391204099488-Product_Image1.png.png" → "17391204099488-Product_Image1";
// "Bottle edited" → "Bottle", so chained edits don't stack the suffix.
export function imageEditBaseName(sourceName) {
  let base = String(sourceName || '').trim();
  const fileParam = base.match(/[?&]file=([^&]+)/);

  if (fileParam) base = decodeURIComponent(fileParam[1]);

  base = base.split('?')[0].split('/').pop();

  while (/\.(png|jpe?g|webp|gif|avif|bmp|tiff?)$/i.test(base)) {
    base = base.replace(/\.[^.]+$/, '');
  }

  while (/\s+edited$/i.test(base)) {
    base = base.replace(/\s+edited$/i, '');
  }

  return base.trim() || 'image';
}

export function imageEditDownloadName(sourceName) {
  const slug = imageEditBaseName(sourceName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'image';

  return `${slug}-edited.png`;
}

// Name in the company's "AI Studio" folder, stamped like aiStudioFileName so
// repeated edits of one image don't pile up as "wine edited (347)". (POR-14659)
export function imageEditLibraryName(sourceName, date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`
    + `-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;

  return `${imageEditBaseName(sourceName)} edited ${stamp}`;
}

// A reload drops the promise that was waiting for the edit: show it as failed
// instead of a spinner that never ends.
export function settleInterruptedImageEdit(edit) {
  if (!edit || edit.status !== 'editing') return edit;

  return { ...edit, status: 'error', error: IMAGE_EDIT_INTERRUPTED };
}
